import React from "react"
import TableHeaderRow from "../../ui/TableHeaderRow"
import TableHeader from "../../ui/TableHeader"
import Table, { Cell, Row } from "../../ui/Table"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { selectAdvancedMode } from "../settings/slice"
import Timestamp from "../../ui/Timestamp"
import Pill from "../../ui/Pill"
import ToggleSwitch from "../../ui/ToggleSwitch"
import { selectRoles, updateUserAccountActive } from "./slice"
import styled from "styled-components"
import TextFilter from "../filters/TextFilter"
import SortHeader from "../sorting/SortHeader"
import { useNavigate } from "react-router-dom"
import theme from "../../ui/theme"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShieldHalved } from "@fortawesome/free-solid-svg-icons"

const SwitchContainer = styled.div`
  transform: scale(0.7);
`

const RoleName = styled.span`
  color: ${(props) => props.color};
`

/**
 * Renders a list of user accounts matching the filters
 *
 * @param accounts
 * @param filter
 * @param setFilter
 * @param stickyHeader
 * @returns {JSX.Element}
 * @constructor
 */
const UserAccountsTable = ({
  accounts = [],
  filter = {},
  setFilter = () => { },
  stickyHeader = true,
  flagLastAccessOlderThan = undefined, // ISO duration, e.g. "PT3H"
}) => {
  const dispatch = useDispatch()

  const advancedMode = useSelector(selectAdvancedMode)
  const roles = useSelector(selectRoles)
  const navigate = useNavigate()
  const navigateTo = (account_id) => () => navigate(`/accounts/${account_id}`)

  const handleSetActive = (id, checked, _etag) => {
    dispatch(updateUserAccountActive({ id, active: checked, _etag }))
  }

  return (
    <Table>
      <TableHeaderRow sticky={stickyHeader}>
        <TableHeader
          filterControl={
            <TextFilter
              filterByName="id_ctns"
              filter={filter}
              setFilter={setFilter}
              active={filter?.name_ctns ? true : false}
            />
          }
          sortControl={
            <SortHeader setFilter={setFilter} filter={filter} sortBy={"id"} />
          }
        >
          Email
        </TableHeader>
        <TableHeader
          filterControl={
            <TextFilter
              filterByName="name_ctns"
              filter={filter}
              setFilter={setFilter}
              active={filter?.name_ctns ? true : false}
            />
          }
          sortControl={
            <SortHeader setFilter={setFilter} filter={filter} sortBy={"name"} />
          }
        >
          Display name
        </TableHeader>
        <TableHeader>Roles</TableHeader>
        <TableHeader title="Two-factor authentication">2FA</TableHeader>
        <TableHeader
          sortControl={
            <SortHeader
              setFilter={setFilter}
              filter={filter}
              sortBy={"created_at"}
            />
          }
        >
          Created At
        </TableHeader>
        <TableHeader>Last accessed</TableHeader>
        <TableHeader>Active</TableHeader>
        {/*Actions*/}
      </TableHeaderRow>
      {accounts && (
        <tbody>
          {accounts.map((account) => (
            <Row key={account.id} zebra>
              <Cell onClick={navigateTo(account.id)}>
                <div>{account.id}</div>
                {account.password_reset_required && (
                  <Pill
                    fgCol={theme.colours.activenew}
                    title="User has to reset password the next time they log in"
                  >
                    password reset requested
                  </Pill>
                )}
              </Cell>
              <Cell onClick={navigateTo(account.id)}>
                {account.display_name}
              </Cell>
              <Cell>
                {account?.roles?.map((r, i) => (
                  <div key={i}>
                    <RoleName color={roles[r.role]?.colorCode}>
                      <FontAwesomeIcon icon={roles[r.role]?.icon} /> {r.role}
                    </RoleName>{" "}
                    {r.ctx && <Pill>{r.ctx}</Pill>}
                  </div>
                ))}
              </Cell>
              <Cell>
                {account?.totp_required ? (
                  <span title="Requires two-factor authentication at login">
                    <FontAwesomeIcon icon={faShieldHalved} /> Enrolled
                  </span>
                ) : (
                  "-"
                )}
              </Cell>
              <Cell onClick={navigateTo(account.id)}>
                <Timestamp utc={advancedMode} columnMode={true}>
                  {account.created_at}
                </Timestamp>
              </Cell>
              <Cell onClick={navigateTo(account.id)}>
                {account.accessed_at && <Timestamp utc={advancedMode} columnMode={true} flagOlderThan={flagLastAccessOlderThan}>
                  {account.accessed_at}
                </Timestamp> || "never"}
              </Cell>
              <Cell>
                <SwitchContainer>
                  <ToggleSwitch
                    handleChange={(checked) =>
                      handleSetActive(account.id, checked, account._etag)
                    }
                    checked={account.is_active}
                    id="mode"
                  />
                </SwitchContainer>
              </Cell>
            </Row>
          ))}
        </tbody>
      )}
    </Table>
  )
}

UserAccountsTable.propTypes = {
  accounts: PropTypes.array, // the accounts to render
  filter: PropTypes.object, // If provided will be used to power the filters if not filters will not be shown
  setFilter: PropTypes.func, // If provided will be used to power the filters if not filters will not be shown
  stickyHeader: PropTypes.bool, // If true, table header is sticky
  hideFilters: PropTypes.bool, // If provided hides the filters for the table
}

export default UserAccountsTable
